import { useMemo } from 'react'
import IssueFix from './IssueFix.jsx'
import { pageIssues, auditIssues, DESCRIPTORS } from './bodyIssues.js'
import styles from './IssueFix.module.css'

/**
 * BodyFixPanel — the page-level issues, each with its door, and the assertion
 * that there are no doors missing.
 *
 * ⚠️ THE ASSERTION IS RENDERED, NOT JUST COMPUTED. Step 3 of this lane asks
 * somebody to walk the surface and confirm every flag has a control. A walk
 * proves it on the day it is walked. Printing `auditIssues()` on the surface
 * proves it on every render, to whoever is looking, including after the next
 * lane adds a flag — and it names the dead end rather than leaving it to be
 * noticed.
 *
 * ⚠️ IT ALSO PRINTS THE RECONCILIATION AND THE DESCRIPTOR LIST. "Nothing is
 * hidden" is only checkable if the surface says what it is NOT counting:
 * descriptors are facts about a body rather than defects in our data, and
 * listing them by name makes "this has no button" a recorded decision instead
 * of something a reader has to assume was deliberate.
 */
export default function BodyFixPanel({
  records = [], totals = null, orphans = [], derived = null, verificationMeta = null,
  verificationSummary = null, verificationError = '', complete = true, onDone,
}) {
  const issues = useMemo(
    () => pageIssues({ records, totals, orphans, derived }),
    [records, totals, orphans, derived],
  )
  const audit = useMemo(() => auditIssues(issues), [issues])
  /* `pageIssues` filters ruled-out records before counting anything and
     reports how many it dropped. Rendering that number is the difference
     between a scope ruling and a quietly shrinking denominator. */
  const ruledOut = issues.ruledOutOfScope || 0

  return (
    <section className={`card ${styles.form}`} aria-label="Problems on this board, and what fixes each one">
      <p className={styles.head}>
        <span className={styles.label}>What is wrong on this board, and the control that ends it</span>
        <span className={styles.count}>{audit.issues}</span>
      </p>

      {/* ⚠️ THE LOAD STATE COMES FIRST. Every count below is of what loaded;
          acting on a partial object is how a legitimate record gets ruled out
          of scope because it was not in the page that arrived. */}
      {!complete && (
        <p className={styles.warn} role="alert">
          THE OBJECT IS NOT FULLY LOADED, so every count below is an undercount and no control here
          should be applied yet. Reload before acting on any of them.
        </p>
      )}

      {issues.map((i) => (
        <IssueFix key={i.id} issue={i} context={{ records: i.records }} onDone={onDone} />
      ))}

      {issues.length === 0 && (
        <p className={styles.note}>
          No page-level issue is open. That is a measured zero, not an empty render — the model
          looked for blank governing bodies, &ldquo;Unspecified&rdquo; bodies, state-level records with no
          state, fused keys, mis-carded acronyms and bodies with no registry row, and found none.
        </p>
      )}

      {/* ---- the assertion ------------------------------------------------ */}
      <p className={audit.clean ? styles.note : styles.warn} role="status">
        <b>{audit.clean ? 'NO DEAD ENDS:' : 'DEAD ENDS PRESENT:'}</b>{' '}
        {audit.issues} {audit.issues === 1 ? 'issue' : 'issues'} across {audit.kinds}{' '}
        {audit.kinds === 1 ? 'kind' : 'kinds'}, covering {audit.recordsFlagged.toLocaleString()} flagged
        records. {audit.withoutControl === 0
          ? 'Every one of them renders with a control that writes to the source of truth.'
          : `${audit.withoutControl} render with no control: ${audit.deadEnds.join(', ')}. That is a bug in this build.`}
        {' '}A record can be flagged by two kinds at once, so that figure is larger than the number of
        distinct records — the same caveat the defect line above carries, stated rather than rounded away.
      </p>

      {/* ---- the scope ruling, stated rather than silently applied -------- */}
      <p className={styles.writes}>
        <b>Ruled out of scope:</b>{' '}
        {ruledOut === 0
          ? 'none. Every record on the object is still counted here — no exclusion has been recorded, which '
            + 'is a measured zero and not an empty render.'
          : `${ruledOut.toLocaleString()} records carry an explicit Out of Scope ruling on sr_scope_status and are `
            + 'excluded from every count above. They are still LIVE and the ruling is reversible — nothing was '
            + 'archived and nothing was deleted. Out of scope is not a gap and not an honest zero: the body '
            + 'genuinely crowns the title, SR just does not sell into it, so it is counted against nobody.'}
        {' '}A record nobody has ruled on reads blank and keeps counting, which is the right direction to be wrong in.
      </p>

      {/* ---- what deliberately has no button ------------------------------ */}
      <p className={styles.writes}>
        <b>Not controls, on purpose:</b> {DESCRIPTORS.join(' · ')}. These describe the body rather than
        asserting anything is wrong with what we hold — a NEPSAC title really is multi-state — so a
        button beside them would imply a defect that is not there.
      </p>

      {verificationError && (
        <p className={styles.warn}>
          The verification rollup could not be read ({verificationError}), so no card can say whether
          its body has ever been checked. Reported as unavailable rather than rendered as clean —
          an absent flag reads as &ldquo;checked and fine&rdquo;.
        </p>
      )}
      {/* ⚠️ THE ESTATE-WIDE VERIFICATION SPLIT, WITH ITS DENOMINATOR AND THE
          HOUR IT WAS PULLED. A cached number presented as current is the lie
          this whole surface is about; the summary route carries `pulledAt` and
          `reconciles` precisely so neither can be implied. */}
      {verificationSummary?.kinds?.body && (() => {
        const b = verificationSummary.kinds.body
        const d = verificationSummary.denominator
        const v = b.byVerdict || {}
        return (
          <p className={v.verified ? styles.writes : styles.warn} role="status">
            <b>Across the estate:</b> {(v.verified || 0).toLocaleString()} records carry a VERIFIED
            governing body, {(v.inferred || 0).toLocaleString()} inferred,{' '}
            {(v.unverified || 0).toLocaleString()} unverified
            {b.unstamped ? `, ${b.unstamped.toLocaleString()} never stamped at all` : ''}.
            {' '}Against a denominator of {d ? d.total.toLocaleString() : '—'} pulled{' '}
            {d ? `${d.ageHours}h ago by ${d.actor}` : 'at an unknown time'}.
            {' '}{b.reconciles === true ? 'The split reconciles to that total.'
              : b.reconciles === false ? 'RECONCILIATION FAILED — do not quote these.'
              : 'No sweep has recorded a denominator, so this cannot be reconciled and is a count of STAMPS, not of the object.'}
            {!v.verified && ' Not one body in the estate has ever been confirmed against its own site — which is what the Verify control on each card is for.'}
          </p>
        )
      })()}

      {verificationMeta && (
        <p className={styles.writes}>
          Verification rollup: {verificationMeta.bodies.toLocaleString()} bodies hold at least one stamp,
          read {verificationMeta.generatedAt ? new Date(verificationMeta.generatedAt).toLocaleString() : 'just now'}.
          {verificationMeta.capped ? ' ⚠️ CAPPED — this is not the whole list.' : ' Not capped: every body with a stamp is in it.'}
        </p>
      )}
    </section>
  )
}
